import React, { useEffect, useState } from "react";
import { ROLE_EDIT_ICON, ROLE_VIEW_ICON } from "../../utils/aap-image-constant";
import TableLayout from "../../components/layouts/table-layout";
import TableHeading from "../../components/common/TableHeading";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../hooks/routes/routes-constant";
import { useTranslation } from "react-i18next";
import { postAllStaff, StaffStatus } from "../../hooks/services/api-services";
import { decryptAEStoJSON, EllipsisWithTooltip, toastEmitter } from "../../utils/utilities";
import { API_RESPONSE, DEFAULT_PAGE_LENGTH } from "../../utils/app-constants";
import { useSelector } from "react-redux";
import AccessPermissionpage from "../../components/common/accessPermissionpage";
import i18n from "../../i18n";

const Staff = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { value } = useSelector((state) => state?.loggedUser);
  const userData = decryptAEStoJSON(value);
  const [rowData, setRowData] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_LENGTH);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState("id");
  const [sortDirection, setSortDirection] = useState("desc");
  const [pending, setPending] = useState(false);

  const [totalRows, setTotalRows] = useState(0);

  const payload = {
    pageIndex: searchTerm === "" ? page || 0 : 0,
    pageSize: pageSize || DEFAULT_PAGE_LENGTH,
    searchText: searchTerm,
    direction: sortDirection || "desc",
    filterByFieldName: sortColumn || "id",
    clinicId: userData?.id,
    doctorId: 0,
    languageType: i18n.language
  };

  const handleStaffStatus = async (id, status) => {
    try {
      const response = await StaffStatus(id, status);

      if (response.status !== 200) {
        toastEmitter("error", response?.data?.message);
      } else {
        toastEmitter("success", response?.data?.message);
        fetchStaffData();
      }
    } catch (err) {
      toastEmitter("error", API_RESPONSE?.MESSAGE_503);
    }
  };

  const tableColumnsStaff = [
    {
      name: t("S.No"),
      width: "100px",
      selector: (row, index) => `${page * pageSize + index + 1}`,
    },

    {
      code: "user_name",
      name: t("Staff Name"),
      sortable: true,
      selector: (row) => (
        <div className="fw-600">

          {row?.userName}
        </div>
      )
    },
    {
      name: t("Mobile Number"),
      sortable: false,
      selector: (row) => (
        row.mobileNo
      )
    },
    {
      name: t("Email"),
      sortable: false,
      selector: (row) =>
      (
        <EllipsisWithTooltip text={`${row.email || "N/A"}`} maxWidth="250px" />

      ),
    },
    {
      name: t("Role"),
      sortable: false,
      selector: (row) => row?.roleResponsePayload?.roleName,
    },
    {
      code: "status",
      sortable: true,
      name: t("Status"),
      selector: (row) => row.status,
      cell: (row) => (

        <div
          className={`d-flex align-items-center gap-1 px-2 py-1 rounded-4 cursor-pointer
    ${row.status === 1
              ? "bg-green-normal color-green-bold"
              : "bg-gray-normal color-gray-bold"
            }
    ${permissionAccess?.moduleAction === 0 ? "disabled-style" : ""}
  `}
          onClick={(e) => {
            if (permissionAccess?.moduleAction !== 0) {
              handleStaffStatus(row?.id, row?.status === 1 ? 0 : 1);
            }
          }}
        >
          <div
            className={`rounded-circle ${row?.status === 1 ? "bg-green-bold" : "bg-gray-bold"
              }`}
            style={{ width: "10px", height: "10px" }}
          ></div>
          <p className="m-0 fs-14 fw-normal">
            {row?.status === 1 ? t("Active") : t("Inactive")}
          </p>
        </div>
      ),
    },

    {
      name: t("Action"),
      width: "120px",
      compact: true,
      cell: (row) => {
        const canEdit = permissionAccess?.updateAction === 1;
        const canView = permissionAccess?.viewAction === 1;

        return (
          <div className="d-flex justify-content-between align-items-center">
            <div
              className={`me-2 cursor-pointer ${!canEdit ? "disabled-style" : ""
                }`}
              onClick={() => {
                if (canEdit) handleEdit(row?.id);
              }}
              style={!canEdit ? { pointerEvents: "none", opacity: 0.6 } : {}}
            >
              <img src={ROLE_EDIT_ICON} alt="Edit" />
            </div>

            <div
              className={`me-2 cursor-pointer ${!canView ? "disabled-style" : ""} `}
              onClick={() => {
                if (canView) handleView(row?.id);
              }}
              style={!canView ? { pointerEvents: "none", opacity: 0.6 } : {}}
            >
              <img src={ROLE_VIEW_ICON} alt="View" />
            </div>
          </div>
        );
      },
    },
  ];

  const fetchStaffData = async () => {
    setPending(true);
    try {
      const response = await postAllStaff(payload);

      if (response.status !== 200) {
        toastEmitter("error", response?.data?.message);
      } else {
        // Safe fallback with null-item filtering
        const rawList = response?.data?.data?.adminResponsePayloadList || [];
        const filteredList = rawList.filter((item) => item !== null);

        const totalElements = response?.data?.data?.totalElement || 0;

        setRowData(filteredList);
        setTotalRows(totalElements); // or totalElements if you want original count
      }
    } catch (err) {
      toastEmitter("error", "Something went wrong. Please try again later.");
    } finally {
      setPending(false);
    }
  };

  useEffect(() => {
    fetchStaffData();
  }, [page, pageSize, searchTerm, sortColumn, sortDirection, i18n.language]);

  const handleEdit = (id) => {
    navigate(`${ROUTES.ROLE_MANAGEMENT}/staff/edit`, {
      state: { formType: "edit", staffId: id, activeTab: "staff" },
    });
  };
  const handleAdd = (id) => {
    navigate(`${ROUTES.ROLE_MANAGEMENT}/staff/add`, {
      state: { formType: "add", activeTab: "staff" },
    });
  };
  const handleView = (id) => {
    navigate(`${ROUTES.ROLE_MANAGEMENT}/staff/view`, {
      state: { formType: "view", staffId: id, activeTab: "staff" },
    });
  };

  // permission code for this module start
  const [permissionAccess, setPermissionAccess] = useState();
  const rawPermission = useSelector((state) => state?.permission?.value);
  const permissions = decryptAEStoJSON(rawPermission);
  useEffect(() => {
    const RoleAccessFilterData =
      permissions && permissions?.filter((v) => v?.moduleName === "Staff Management");
    setPermissionAccess(RoleAccessFilterData?.[0]);
  }, []);
  // permission code for this module end
  return (
    <>
      {permissionAccess?.addAction === 0 &&
        permissionAccess?.updateAction === 0 &&
        permissionAccess?.viewAction === 0 ? (
        <>
          <AccessPermissionpage />
        </>
      ) : (
        <>
          <TableHeading
            title={t("Staff")}
            searchValue={searchTerm}
            setSearchValue={setSearchTerm}
            data="Staff"
            showbutton={permissionAccess?.addAction === 1}
            addButtonClick={() => handleAdd()}
          />
          <div className="table-wrapper">
            <TableLayout
              _tblColumns={tableColumnsStaff}
              _rowData={rowData}
              pending={pending}
              pagination={true}
              selectableRows={false}
              setPending={setPending}
              _totalRows={totalRows}
              pageSize={pageSize}
              setPageSize={setPageSize}
              setPage={setPage}
              setSortColumn={setSortColumn}
              setSortDirection={setSortDirection}
            />
          </div>
        </>
      )}
    </>
  );
};

export default Staff;
